'use client';

// Utilidades y modales compartidos del MÓDULO 6 (Notificaciones), usados tanto por
// la Central de Notificaciones (Secretaría) como por el panel del expediente:
//  - badgeEstadoNotif / fmtFechaNotif: presentación.
//  - ModalCambiarEstado: PATCH /notificaciones/:id/estado.
//  - ModalConstancia:    POST  /notificaciones/:id/constancia.
// Los errores 422 del backend llegan como { message } y se muestran como toast.

import { useState } from 'react';
import { FileCheck2, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import {
  cambiarEstadoNotificacion, registrarConstancia,
  ESTADO_NOTIFICACION, ESTADO_LABEL, radicadoDe,
  type Notificacion, type EstadoNotificacion,
} from '@/lib/notificaciones';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

// ---- Presentación ---------------------------------------------------------

export function fmtFechaNotif(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO');
}

// Colores por estado: ENTREGADA verde · ENVIADA/PENDIENTE ámbar · DEVUELTA/FALLIDA rojo.
export function badgeEstadoNotif(estado: string) {
  const e = (estado || '').toUpperCase();
  const label = ESTADO_LABEL[e as keyof typeof ESTADO_LABEL] ?? estado;
  if (e === 'ENTREGADA') {
    return <Badge className="bg-primary text-primary-foreground border-transparent">{label}</Badge>;
  }
  if (e === 'ENVIADA' || e === 'PENDIENTE') {
    return <Badge className="bg-amber-500 text-white border-transparent">{label}</Badge>;
  }
  if (e === 'DEVUELTA' || e === 'FALLIDA') {
    return <Badge className="bg-red-600 text-white border-transparent">{label}</Badge>;
  }
  return <Badge variant="secondary">{label}</Badge>;
}

// ---- Modal: cambiar estado ------------------------------------------------

export function ModalCambiarEstado({ notificacion, onClose, onListo }: {
  notificacion: Notificacion; onClose: () => void; onListo: () => void;
}) {
  const inicial = ESTADO_NOTIFICACION.includes(notificacion.estado as EstadoNotificacion)
    ? (notificacion.estado as EstadoNotificacion)
    : 'ENVIADA';
  const [estado, setEstado] = useState<EstadoNotificacion>(inicial);
  const [fechaNotificacion, setFechaNotificacion] = useState('');
  const [guardando, setGuardando] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      await cambiarEstadoNotificacion(notificacion.id, {
        estado,
        ...(fechaNotificacion ? { fechaNotificacion } : {}),
      });
      toast.success('Estado de la notificación actualizado.');
      onListo();
    } catch (err) {
      toast.error('No se pudo cambiar el estado', {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCcw className="size-5 text-primary" /> Cambiar estado
          </DialogTitle>
          <DialogDescription>Radicado: <strong>{radicadoDe(notificacion)}</strong></DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div className="space-y-2">
            <Label>Estado</Label>
            <Select value={estado} onValueChange={(v) => setEstado(v as EstadoNotificacion)}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ESTADO_NOTIFICACION.map((e) => (
                  <SelectItem key={e} value={e}>{ESTADO_LABEL[e]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="fn">Fecha de notificación (opcional)</Label>
            <Input id="fn" type="date" value={fechaNotificacion} onChange={(e) => setFechaNotificacion(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Modal: constancia ----------------------------------------------------

export function ModalConstancia({ notificacion, onClose, onListo }: {
  notificacion: Notificacion; onClose: () => void; onListo: () => void;
}) {
  const [constanciaKey, setConstanciaKey] = useState('');
  const [fechaNotificacion, setFechaNotificacion] = useState('');
  const [guardando, setGuardando] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      await registrarConstancia(notificacion.id, {
        ...(constanciaKey.trim() ? { constanciaKey: constanciaKey.trim() } : {}),
        ...(fechaNotificacion ? { fechaNotificacion } : {}),
      });
      toast.success('Constancia registrada.');
      onListo();
    } catch (err) {
      toast.error('No se pudo registrar la constancia', {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck2 className="size-5 text-primary" /> Registrar constancia
          </DialogTitle>
          <DialogDescription>Radicado: <strong>{radicadoDe(notificacion)}</strong></DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ck">Constancia (key, opcional)</Label>
            <Input id="ck" value={constanciaKey} onChange={(e) => setConstanciaKey(e.target.value)} placeholder="Clave del soporte en almacenamiento" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cfn">Fecha de notificación (opcional)</Label>
            <Input id="cfn" type="date" value={fechaNotificacion} onChange={(e) => setFechaNotificacion(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
